import React from 'react';
import { TextField, CircularProgress, InputAdornment } from '@mui/material';
import { useGoogleMapsPlaces } from '@/hooks/useGoogleMapsPlaces';
import { useLocalization } from '@/hooks/useLocalization';
import { outlinedInputRootSx } from '@/constants/dimensions';
import { GoogleAutocomplete } from '@/hooks/useGoogleMapsPlaces';

export interface AddressAutocompleteProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  name?: string;
  error?: boolean;
  helperText?: string;
}

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled,
  fullWidth = true,
  name,
  error,
  helperText,
}) => {
  const { t } = useLocalization();
  const { loaded, error: mapsError } = useGoogleMapsPlaces();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const autocompleteRef = React.useRef<GoogleAutocomplete | null>(null);

  React.useEffect(() => {
    if (
      !window.google?.maps ||
      !loaded ||
      !inputRef.current ||
      autocompleteRef.current
    )
      return;
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ['geocode'],
      fields: ['formatted_address'],
    });
    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      const formatted =
        place?.formatted_address || inputRef.current?.value || '';
      onChange(formatted);
    });
    autocompleteRef.current = ac;
  }, [loaded, onChange]);

  const labelSlotProps = { inputLabel: { shrink: true } };

  const commonProps = {
    label,
    name,
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    onBlur,
    fullWidth,
    slotProps: labelSlotProps,
    disabled,
    error,
    helperText,
    sx: outlinedInputRootSx,
    placeholder:
      placeholder ?? t('addressLookupPlaceholder', 'Start typing to search...'),
    autoComplete: 'off',
  };

  if (mapsError) {
    return (
      <TextField
        {...commonProps}
        inputRef={inputRef}
        helperText={
          helperText ??
          t(
            'addressAutocompleteUnavailable',
            'Address lookup unavailable. You can still type an address manually.',
          )
        }
      />
    );
  }

  return (
    <TextField
      {...commonProps}
      inputRef={inputRef}
      slotProps={{
        ...labelSlotProps,
        input: {
          endAdornment: !loaded ? (
            <InputAdornment position='end'>
              <CircularProgress size={18} />
            </InputAdornment>
          ) : undefined,
        },
      }}
    />
  );
};

export default AddressAutocomplete;
